import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { StatusBadge } from '@/components/ui/status-badge';
import type { billing_DunningCockpitItemResponse } from '@/api/models/billing_DunningCockpitItemResponse';
import { DebtorAccountLink } from '@/components/shared/entity-links';
import { formatMoneyCents } from '@/lib/money';
import { formatDateMedium } from '@/lib/format';
import { useCurrency } from '@/hooks/useCurrency';
import { DUNNING_RUN_STATUS, dunningStageStatus } from './stageBadge';
import { formatHouseholdLabel } from './householdLabel';

interface Props {
  item: billing_DunningCockpitItemResponse;
}

export function DunningWorkflowHeader({ item }: Props) {
  const { i18n } = useLingui();
  const stage = item.current_state ?? '';
  const stageStatus = dunningStageStatus(stage);
  const outstanding = item.outstanding_cents ?? 0;
  const { currency: orgCurrency } = useCurrency();
  const currency = item.currency || orgCurrency;
  const householdLabel = formatHouseholdLabel(item, i18n) || '—';
  const debtorId = item.debtor_account?.id ?? item.debtor_account_id;

  return (
    <div className="space-y-2" data-testid="dunning-workflow-header">
      {/* Link the household out to its Reconciliation debtor account so the
          drawer is no longer a dead-end (you can jump to the family's ledger). */}
      <h2 className="text-xl font-semibold">
        {debtorId ? (
          <DebtorAccountLink
            debtorId={debtorId}
            displayName={householdLabel}
            className="font-semibold"
          />
        ) : (
          householdLabel
        )}
      </h2>
      <div className="flex flex-wrap gap-2 items-center text-sm">
        <span data-testid="stage-badge">
          {stageStatus ? (
            <StatusBadge tone={stageStatus.tone} icon={stageStatus.icon} label={i18n._(stageStatus.label)} />
          ) : (
            <StatusBadge tone="neutral" label={item.stage_label || stage} />
          )}
        </span>
        <span className="font-mono">{formatMoneyCents(outstanding, currency)}</span>
        {item.is_paused && (
          <StatusBadge
            tone={DUNNING_RUN_STATUS.paused.tone}
            icon={DUNNING_RUN_STATUS.paused.icon}
            label={
              item.is_paused_indefinite
                ? i18n._(DUNNING_RUN_STATUS.pausedIndefinitely.label)
                : i18n._(
                    msg({ id: 'dunning.cockpit.pausedUntil', message: 'Paused until {date}' }),
                    { date: item.paused_until ? formatDateMedium(item.paused_until, i18n.locale) : '—' },
                  )
            }
          />
        )}
        {item.is_suppressed && (
          <StatusBadge
            tone={DUNNING_RUN_STATUS.suppressed.tone}
            icon={DUNNING_RUN_STATUS.suppressed.icon}
            label={i18n._(DUNNING_RUN_STATUS.suppressed.label)}
          />
        )}
        {item.is_send_failed && (
          <StatusBadge
            tone={DUNNING_RUN_STATUS.sendFailed.tone}
            icon={DUNNING_RUN_STATUS.sendFailed.icon}
            label={i18n._(DUNNING_RUN_STATUS.sendFailed.label)}
          />
        )}
      </div>
    </div>
  );
}
